
import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, Loader2, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = 'service_uqxsfye';
const EMAILJS_TEMPLATE_ID = 'template_85zgv0q';
const EMAILJS_PUBLIC_KEY = 'Eu5fFSn1lUKgdhZCP';

const ContactPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('지자체/공공기관');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setIsSubmitting(true);

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      setIsSuccess(true);
      if (formRef.current) formRef.current.reset();
      
      // 5초 후 성공 메시지 숨김
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Email sending failed:', error);
      alert('메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="py-24 bg-btBlue text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-btOrange/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl font-black mb-6">함께 만드는 탄소중립</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            블루테라의 솔루션이 궁금하신가요? <br />
            귀사의 상황에 맞는 맞춤형 상담을 제공해드립니다.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-16">
          <div className="lg:col-span-1 space-y-12">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-6">Contact Info</h2>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg"><Mail className="h-6 w-6 text-btBlue" /></div>
                  <div>
                    <div className="text-xs text-gray-400 font-bold uppercase">Email</div>
                    <div className="text-gray-900 font-bold">contact@blueterra.kr</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg"><Phone className="h-6 w-6 text-btBlue" /></div>
                  <div>
                    <div className="text-xs text-gray-400 font-bold uppercase">Phone</div>
                    <div className="text-gray-900 font-bold">070-7637-5678</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg"><MapPin className="h-6 w-6 text-btBlue" /></div>
                  <div>
                    <div className="text-xs text-gray-400 font-bold uppercase">Office</div>
                    <div className="text-gray-900 font-bold text-sm">경북 구미시 대학로 61, 두레관 302-16호(양호동, 금오공과대학교)</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-btLightGray rounded-2xl">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-btOrange" /> 빠르게 답변드리겠습니다. 
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                문의를 남겨주시면 담당 전문가가 영업일 기준 24시간 이내에 연락드립니다.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="mb-10 flex gap-4 overflow-x-auto pb-2">
              {['지자체/공공기관', '기업 ESG', '일반 문의'].map((tab) => (
                <button 
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-xl font-bold whitespace-nowrap transition-all ${activeTab === tab ? 'bg-btBlue text-white shadow-lg' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  {tab} 문의
                </button>
              ))}
            </div>

            {isSuccess ? (
              <div className="bg-green-50 border-2 border-green-100 rounded-2xl p-12 text-center animate-in fade-in zoom-in duration-300">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 text-green-600 rounded-full mb-6">
                  <CheckCircle className="h-10 w-10" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-4">문의가 정상적으로 접수되었습니다!</h3>
                <p className="text-gray-600 mb-8">내용을 확인한 후 담당자가 곧 연락드리겠습니다. 블루테라를 찾아주셔서 감사합니다.</p>
                <button 
                  onClick={() => setIsSuccess(false)}
                  className="px-8 py-3 bg-white border-2 border-green-200 text-green-700 font-bold rounded-xl hover:bg-green-50 transition-colors"
                >
                  새로운 문의 남기기
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                {/* Hidden input to pass category to EmailJS */}
                <input type="hidden" name="contact_category" value={activeTab} />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">담당자 성함</label>
                    <input 
                      required
                      name="user_name"
                      type="text" 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-btBlue focus:ring-2 focus:ring-btBlue/20 outline-none transition-all" 
                      placeholder="홍길동" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">연락처</label>
                    <input 
                      required
                      name="user_phone"
                      type="text" 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-btBlue focus:ring-2 focus:ring-btBlue/20 outline-none transition-all" 
                      placeholder="010-0000-0000" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">이메일 주소</label>
                    <input 
                      required
                      name="user_email"
                      type="email" 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-btBlue focus:ring-2 focus:ring-btBlue/20 outline-none transition-all" 
                      placeholder="example@email.com" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">소속 (기관/기업명)</label>
                    <input 
                      required
                      name="user_org"
                      type="text" 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-btBlue focus:ring-2 focus:ring-btBlue/20 outline-none transition-all" 
                      placeholder="BlueTerra 기획팀" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">문의 내용</label>
                  <textarea 
                    required
                    name="message"
                    rows={6} 
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-btBlue focus:ring-2 focus:ring-btBlue/20 outline-none transition-all resize-none" 
                    placeholder="문의하실 내용을 자유롭게 적어주세요."
                  ></textarea>
                </div>
                <button 
                  disabled={isSubmitting}
                  type="submit" 
                  className={`w-full py-4 bg-btOrange text-white font-black text-lg rounded-xl shadow-lg shadow-btOrange/20 transition-all flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-orange-600'}`}
                >
                  {isSubmitting ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> 전송 중...</>
                  ) : (
                    <><Send className="h-5 w-5" /> 문의하기</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
